import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FiMoon,
  FiSun,
  FiX,
  FiLogOut,
  FiMenu,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import sideMenu from '../../config/sideMenu';
import { SideMenuItem } from '../../interfaces/config/sideMenu';
import { useTheme } from '../../shared/context/ThemeContext';
import LMSCore from '../../core/lmsCore';
import tokenService from '../../shared/services/tokenService';
import { showLoading, hideLoading } from '../../connected-components/loader/reducer';
import { store } from '../../redux/store';
import { Library } from 'lucide-react';
import logo from '../../assets/images/logo.png';

/**
 * MainLayout component with InsightHub theme styling
 * Features a modern sidebar design with smooth animations
 *
 * @component
 * @example
 * ```tsx
 * <MainLayout />
 * ```
 */
export default function MainLayout() {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Using the sideMenu items from your config
  const navItems = sideMenu;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleCollapsed = () => {
    if (!isMobile) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Show loading indicator
      store.dispatch(showLoading());
      
      await LMSCore.logout();
      await tokenService.removeToken();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, remove token and redirect
      await tokenService.removeToken();
      navigate('/login');
    } finally {
      // Hide loading indicator
      store.dispatch(hideLoading());
    }
  };

  const getCurrentPageTitle = () => {
    // Check if we're on a course details page
    if (location.pathname.startsWith('/courses/') && location.pathname.split('/').length > 3) {
      // Extract course title from URL and convert back to readable format
      const pathParts = location.pathname.split('/');
      const courseTitle = pathParts[2]; // This is the courseTitle parameter
      if (courseTitle) {
        // Convert URL-friendly format back to readable title
        return courseTitle.split('-').join(' ');
      }
    }
    
    // Default behavior for other routes
    const currentItem = navItems.find(item => item.path === location.pathname);
    return currentItem ? t(currentItem.title) : t('app.name');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div
          className={`
            flex flex-col h-full transition-all duration-300 ease-in-out
            ${
              isMobile
                ? `fixed top-0 left-0 h-full w-72 bg-white dark:bg-black z-50 transform transition-transform duration-300 ease-in-out ${
                    mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                  }`
                : `bg-white dark:bg-black ${isCollapsed ? 'w-16' : 'w-72'}`
            }
          `}
        >
          {/* Logo Section */}
          <div className={`flex items-center ${isCollapsed && !isMobile ? 'justify-center' : 'justify-start'} px-4 py-6 h-[64px] flex-shrink-0`}>
            {isCollapsed && !isMobile ? (
              <img src={logo} width={28} className="text-primary" />
            ) : (
              <>
               <img src={logo} width={28} className="text-primary" />
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('app.name')}
                </span>
              </>
            )}
          </div>

          {/* Navigation - scrollable */}
          <div className="flex-1 min-h-0 px-2 overflow-y-auto">
            <nav className="space-y-1">
              {navItems.map((item: SideMenuItem) => {
                const isActive = location.pathname === item.path;
                const IconComponent = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={`
                      w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group
                      ${
                        isActive
                          ? "bg-primary text-white"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800 dark:hover:text-gray-200"
                      }
                    `}
                  >
                    <div className="flex items-center w-full">
                      <div
                        className={`min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300 ${
                          isActive ? "bg-white/20 rounded" : ""
                        }`}
                      >
                        {IconComponent && (
                          <IconComponent
                            size={16}
                            className={
                              isActive
                                ? "text-white"
                                : "text-gray-500 dark:text-gray-400"
                            }
                          />
                        )}
                      </div>
                      <span className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed && !isMobile ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
                        {t(item.title)}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-100 dark:border-gray-900 px-2 py-4 flex-shrink-0">
            <div className="space-y-1">
              {/* Theme Toggle */}
              <button
                onClick={toggleDarkMode}
                className="w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <div className="flex items-center w-full">
                  <div className="min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                    {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
                  </div>
                  <span className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed && !isMobile ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
                    {isDarkMode ? t('app.lightMode') : t('app.darkMode')}
                  </span>
                </div>
              </button>
              
              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-2 py-2 text-left rounded-lg transition-all duration-300 text-sm group text-gray-600 dark:text-gray-400 hover:bg-gray-400/40 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <div className="flex items-center w-full">
                  <div className="min-w-[32px] h-8 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                    <FiLogOut size={16} />
                  </div>
                  <span className={`ml-2 transition-all duration-300 whitespace-nowrap ${isCollapsed && !isMobile ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}>
                    {t('app.logout')}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        {!isMobile && (
          <div className={`fixed top-0 right-0 h-16 bg-white dark:bg-black border-b border-gray-100 dark:border-gray-900 px-6 flex items-center z-40 transition-all duration-300 ease-in-out ${isCollapsed ? 'left-16' : 'left-72'}`}>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                  <Library size={18} className="text-primary" />
                </div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {getCurrentPageTitle()}
                </h1>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Header */}
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-black px-4 flex items-center justify-between z-40 border-b border-gray-100 dark:border-gray-900">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                <Library size={18} className="text-primary" />
              </div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getCurrentPageTitle()}
              </h1>
            </div>
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-400/40 transition-colors duration-200"
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        )}

        {/* Mobile Overlay */}
        {isMobile && mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={closeMobileMenu}
          />
        )}

        {/* Main Content */}
        <div
          className={`flex-1 ${isMobile ? "pt-16" : "pt-16"} dark:bg-darkBg overflow-y-auto px-6 transition-all duration-300 ease-in-out`}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}